import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { MOCK_MEETINGS, MOCK_TRANSCRIPT, MOCK_ACTIONS, MOCK_DECISIONS } from '../data/mockData';

const { FiArrowLeft, FiPlay, FiVolume2, FiMaximize, FiCheckSquare, FiTarget, FiFileText, FiShare2, FiDownload, FiLoader, FiCheckCircle, FiAlertCircle } = FiIcons;

export default function MeetingConsole() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('actions');
  const [procState, setProcState] = useState('idle'); // idle, processing, success
  
  const meeting = MOCK_MEETINGS.find(m => m.id === id) || MOCK_MEETINGS[0];

  const simulateProcessing = () => {
    setProcState('processing');
    setTimeout(() => {
      setProcState('success');
    }, 2500);
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'decision': return 'text-emerald-400 border-emerald-400/30 bg-emerald-400/5';
      case 'action': return 'text-amber-400 border-amber-400/30 bg-amber-400/5';
      case 'warning': return 'text-rose-400 border-rose-400/30 bg-rose-400/5';
      case 'insight': return 'text-cyan-400 border-cyan-400/30 bg-cyan-400/5';
      default: return 'text-gray-300 border-transparent hover:bg-white/5';
    }
  };

  return (
    <div className="h-full flex flex-col -m-4 md:-m-6 lg:-m-8 relative">
      {/* Console Header */}
      <header className="glass-panel rounded-none border-x-0 border-t-0 p-4 flex items-center justify-between z-20 shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/library')} className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors">
            <SafeIcon icon={FiArrowLeft} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white flex items-center gap-2">
              {meeting.title}
              <span className={`text-[10px] px-2 py-0.5 rounded font-mono border transition-colors ${
                procState === 'success' ? 'bg-emerald-400/20 text-emerald-400 border-emerald-400/50' : 'bg-cyan-400/20 text-cyan-400 border-cyan-400/50'
              }`}>
                {procState === 'success' ? 'VERIFIED' : meeting.status.toUpperCase()}
              </span>
            </h1>
            <div className="text-xs text-gray-400 font-mono mt-1">
              {new Date(meeting.date).toLocaleString()} • {meeting.duration}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {procState === 'idle' && (
            <button 
              onClick={simulateProcessing}
              className="px-4 py-1.5 bg-cyan-400 text-black text-xs font-bold rounded hover:shadow-neon transition-all"
            >
              RUN ANALYSIS
            </button>
          )}
          <button className="p-2 text-gray-400 hover:text-cyan-400 transition-colors"><SafeIcon icon={FiShare2} /></button>
          <button className="p-2 text-gray-400 hover:text-cyan-400 transition-colors"><SafeIcon icon={FiDownload} /></button>
        </div>
      </header>

      {/* Processing Overlay Simulation */}
      <AnimatePresence>
        {procState === 'processing' && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 bg-[#0D0518]/90 backdrop-blur-md flex flex-col items-center justify-center"
          >
            <div className="w-64 h-2 bg-white/10 rounded-full overflow-hidden mb-6">
              <motion.div 
                initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 2.5 }}
                className="h-full bg-cyan-400 shadow-neon"
              ></motion.div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <SafeIcon icon={FiLoader} className="text-cyan-400 text-3xl animate-spin" />
              <span className="text-white font-mono text-sm tracking-widest">EXTRACTING INTELLIGENCE...</span>
              <p className="text-gray-500 text-[10px] font-mono mt-2">MAPPING SPEAKER VOICES • IDENTIFYING ACTION VERBS</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Split View */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden relative">
        <div className="w-full lg:w-1/2 flex flex-col border-r border-matrix-border/50 bg-[#0D0518]">
          <div className="h-48 md:h-64 bg-black relative flex flex-col border-b border-matrix-border/50 shrink-0">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1200&q=80')] bg-cover bg-center opacity-30"></div>
            <div className="mt-auto p-4 relative z-10 bg-gradient-to-t from-black to-transparent flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button className="w-10 h-10 rounded-full bg-cyan-400 text-black flex items-center justify-center hover:shadow-neon transition-shadow">
                  <SafeIcon icon={FiPlay} className="ml-1" />
                </button>
                <div className="font-mono text-sm text-white">14:02 / 45:00</div>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <SafeIcon icon={FiVolume2} className="hover:text-white cursor-pointer" />
                <SafeIcon icon={FiMaximize} className="hover:text-white cursor-pointer" />
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-custom p-4 space-y-4">
            <div className="sticky top-0 bg-[#0D0518]/90 backdrop-blur-sm py-2 z-10 mb-4 border-b border-white/5 flex justify-between items-center">
              <h3 className="text-xs font-mono text-gray-500 uppercase tracking-widest">Live Transcript</h3>
              {procState === 'success' && <span className="text-[10px] text-emerald-400 font-mono">SYNCED</span>}
            </div>
            
            {MOCK_TRANSCRIPT.map((entry) => (
              <div key={entry.id} className={`p-3 rounded-lg border transition-all duration-500 ${getTypeColor(entry.type)} ${procState === 'processing' ? 'blur-sm opacity-50' : 'blur-0 opacity-100'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold">{entry.speaker}</span>
                  <span className="text-[10px] font-mono opacity-50">{entry.time}</span>
                </div>
                <p className={`text-sm ${entry.type === 'neutral' ? 'text-gray-300' : 'text-white'}`}>{entry.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full lg:w-1/2 flex flex-col bg-violet-950/20">
          <div className="flex p-4 gap-2 border-b border-matrix-border/50 overflow-x-auto scrollbar-hide">
            {[
              { id: 'actions', label: 'Actions', icon: FiCheckSquare },
              { id: 'decisions', label: 'Decisions', icon: FiTarget },
              { id: 'notes', label: 'Smart Notes', icon: FiFileText },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-cyan-400/10 text-cyan-400 border-cyan-400/50' : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'}`}>
                <SafeIcon icon={tab.icon} />{tab.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-custom p-6">
            <AnimatePresence mode="wait">
              <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-4">
                {activeTab === 'actions' && (
                  <>
                    <div className="text-xs font-mono text-cyan-400 mb-4 bg-cyan-400/10 inline-block px-2 py-1 rounded">
                      {MOCK_ACTIONS.filter(a => a.meeting === meeting.title).length} ACTIONS DETECTED
                    </div>
                    {MOCK_ACTIONS.filter(a => a.meeting === meeting.title).map(action => (
                      <div key={action.id} className="glass-panel p-4 rounded-xl flex gap-4 items-start group hover:border-cyan-400/30 transition-colors">
                        <div className="mt-1 w-5 h-5 rounded border border-gray-500 flex items-center justify-center cursor-pointer group-hover:border-cyan-400">
                          {action.status === 'completed' && <SafeIcon icon={FiCheckCircle} className="text-cyan-400" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-white font-medium mb-1">{action.task}</p>
                          <span className="text-[10px] text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded font-mono">DUE: {action.due}</span>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {activeTab === 'decisions' && (
                  MOCK_DECISIONS.filter(d => d.meeting === meeting.title).map(decision => (
                    <div key={decision.id} className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 relative overflow-hidden">
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500"></div>
                      <p className="text-sm text-white font-medium pl-2">{decision.text}</p>
                      <div className="mt-2 pl-2"><span className="text-[10px] font-mono px-2 py-0.5 rounded bg-black/40 text-rose-400">IMPACT: {decision.impact}</span></div>
                    </div>
                  ))
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}